"use strict";

const Backbone = require("backbone");
const _ = require("underscore");
const moment = require("moment");
const DayModel = require("site/page/checkout/day-model");

const WeekdaySelectionCollection = Backbone.Collection.extend({
  model: DayModel,

  dateFormat: "YYYY-MM-DD",

  initialize () {
    this.initialWeek = 0;
    this.maxWeeks = 0;
    this.selectedWeek = 0;
    this.setInitialSelectedDate();

    this.on("reset", () => {
      this.updateInitialWeek();
      this.updateMaxWeeks();
    });
  },

  setInitialSelectedDate () {
    var dateFromUrl = new URLSearchParams(window.location.hash.substr(1));
    const dateValue = dateFromUrl.get("date");
    if (dateValue) {
      this.selectedDate = dateValue;
    } else {
      this.selectedDate = this.formatDate(moment());
    }
  },

  setSelectedWeek (date) {
    this.selectedWeek = this.weekNumber(date);
  },

  setSelectedDate (date) {
    this.selectedDate = date;
    this.setSelectedWeek(moment(date));
  },

  updateInitialWeek () {
    this.initialWeek = this.weekNumber(moment(this.getFirstDay().date));
  },

  updateMaxWeeks () {
    this.maxWeeks = this.weekNumber(moment(this.getLastDay().date));
  },

  getDayModelByDate (date) {
    return _.first(this.where({ date: date }));
  },

  updateEmployeeDays (selectedEmployee) {
    _.each(this.models, (day) => {
      let isEmployeeAvailable;
      let isAvailable = false;

      // if there are no slots, day is not available
      if (!day.get("slots") || day.get("slots").length === 0) {
        day.set("available", isAvailable);
        return;
      }

      isAvailable = true;

      // if employee is selected, check employee availability for each service
      if (selectedEmployee) {
        const daySlots = day.get("slots");
        isEmployeeAvailable = _.some(
          daySlots,
          (slot) => {
            return _.contains(slot.employeeIds, selectedEmployee.employeeId);
          }
        );
      } else {
        isEmployeeAvailable = true;
      }

      day.set({
        selected: this.selectedDate === day.get("date"),
        available: isAvailable && isEmployeeAvailable
      });
    });
  },

  prependDayData ({ days, referenceSlots }) {
    const newDays = this.removePlaceholderDays(
      this.transformDayData({ days, referenceSlots })
    );
    const currentDays = this.toJSON();
    this.reset([...newDays, ...currentDays]);
  },

  appendDayData ({ days, referenceSlots }) {
    const newDays = this.transformDayData({ days, referenceSlots });
    const currentDays = this.removePlaceholderDays(this.toJSON());
    this.reset([...currentDays, ...newDays]);
  },

  resetDayData ({ days, referenceSlots }) {
    this.reset(this.transformDayData({ days, referenceSlots }));
  },

  removePlaceholderDays (days) {
    return _.reject(days, (day) => day.isPlaceholderDate);
  },

  transformDayData ({ days, referenceSlots }) {
    let transformedDays = [];

    _.each(days, (day) => {
      const curDate = moment({
        year: day.year,
        month: day.month,
        day: day.day
      });
      const isAvailable = Boolean(day.startTimes && day.startTimes.length > 0);

      if (!this.selectedDate && isAvailable) {
        this.selectedDate = this.formatDate(curDate);
      }

      if (curDate.isSame(this.selectedDate)) {
        this.setSelectedWeek(curDate);
      }

      const dayObject = {
        date: curDate.format(this.dateFormat),
        selected: curDate.isSame(this.selectedDate),
        available: isAvailable,
        weekdayName: curDate.format("ddd"),
        day: curDate.format("D")
      };

      if (day.startTimes) {
        dayObject.slots = createDaySlots({
          startTimes: day.startTimes,
          referenceSlots
        });
      }

      transformedDays.push(dayObject);
    });

    // check last day:
    const daysUntilEndOfWeek = getDaysUntilEndOfWeek.call(
      this,
      _.last(days).date
    );

    if (daysUntilEndOfWeek.length > 0) {
      transformedDays = _.union(transformedDays, daysUntilEndOfWeek);
    }

    return transformedDays;
  },

  getFirstDay () {
    return this.at(0).toJSON();
  },

  getLastDay () {
    return this.at(this.length - 1).toJSON();
  },

  getFirstAvailableDay () {
    return _.find(
      this.models,
      _.bind(function (model) {
        return model.get("available");
      }, this)
    );
  },

  // Construct a week number, suitable for gt and lt comparison with
  // other week numbers. It must take the year in to account.
  //
  // For ease of debugging, the number is readable.
  // For example 201703 is the third week in the year 2017.
  weekNumber (day) {
    return 100 * day.isoWeekYear() + day.isoWeek();
  },

  formatDate (date) {
    return date.format(this.dateFormat);
  }
});

// Export this collection as a single instance so it can be accessed in
// different modules. We need to add data to it before it gets to the view so
// it can be transformed, so attaching it to the `{collection: <object>}`
// property will not work
module.exports = new WeekdaySelectionCollection();

function createDaySlots ({ startTimes, referenceSlots }) {
  const slots = [];

  // go though each slot and add employees
  _.each(startTimes, (slot) => {
    const curSlot = {
      startTime: slot.startTime,
      services: []
    };

    if (slot.serviceAvailabilitySlots) {
      _.each(slot.serviceAvailabilitySlots, (service) => {
        curSlot.services.push(
          _.extend(service, referenceSlots[service.referenceSlotId])
        );

        if (service.skuSlots) {
          service.skus = [];

          _.each(service.skuSlots, (sku) => {
            service.skus.push(
              _.extend(sku, referenceSlots[sku.referenceSlotId])
            );
          });
        }
      });
    }

    slots.push(_.extend(curSlot, referenceSlots[slot.referenceSlotId]));
  });

  return slots;
}

function getDaysUntilEndOfWeek (date) {
  var nextEndOfWeek, daysToAdd, addingDay;
  var days = [];

  // get the last day"s day of week
  nextEndOfWeek = moment(date).isoWeekday(7);
  // get the days to add
  daysToAdd = nextEndOfWeek.diff(moment(date), "days");

  if (daysToAdd === 0) {
    return days;
  }

  for (var i = 1, li = daysToAdd + 1; i < li; ++i) {
    addingDay = moment(date).add(i, "days");

    days.push({
      date: addingDay.format(this.dateFormat),
      selected: false,
      available: false,
      isPlaceholderDate: true,
      weekdayName: addingDay.format("ddd"),
      day: addingDay.format("D")
    });
  }

  return days;
}
